// BOOKING COPY AND OFFERS (Run O §2). Pure.
//
// ⚠️⚠️ THE CONFIRMATION SMS IS A SERVER TEMPLATE, NEVER THE MODEL'S TEXT. If the model
// wrote it, it could say "you're booked" before the booking exists, or after it
// failed. On a successful booking the model's reply is discarded for that turn.

import type { BusinessProfile } from '../../types/businessProfile.js';
import type { AvailabilitySlot } from '../../types/appointments.js';
import { localDateString } from './promotions.js';

/** "Thu 16 Oct 10:30am" in the calendar's own zone. */
export function formatSlotLine(startsAtIso: string, timezone: string): string {
  const d = new Date(startsAtIso);
  const parts = new Intl.DateTimeFormat('en-AU', {
    timeZone: timezone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(d);
  const get = (t: string): string => parts.find((p) => p.type === t)?.value ?? '';
  const period = get('dayPeriod').toLowerCase().replace(/\./g, '');
  return `${get('weekday')} ${get('day')} ${get('month')} ${get('hour')}:${get('minute')}${period}`;
}

export const MAX_OFFERED_SLOTS = 15;
export const MAX_SLOTS_PER_DAY = 3;
export const OFFER_HORIZON_DAYS = 7;

export interface OfferedSlot {
  id: string; // S1, S2, ...
  slot: AvailabilitySlot;
  line: string; // "S1: Thu 16 Oct 10:30am"
}

/**
 * Up to 15 offered slots over the next 7 days, at most 3 per day, in date order, each with a short id
 * the model can name. The server validates the id against exactly this set.
 *
 * ⚠️ SPREAD ACROSS DAYS, NOT A CHRONOLOGICAL SLICE. A flat slice of the first N slots is filled by
 * today alone at 30-minute intervals, so a lead asking for "tomorrow" could never be offered it (the
 * live simulator's persona 7). Within a day the 3 are spread out (early / middle / late of what is
 * free), not three consecutive half-hours.
 */
export function buildOfferedSlots(
  slots: readonly AvailabilitySlot[],
  timezone: string,
  nowMs: number,
): OfferedSlot[] {
  const horizon = nowMs + OFFER_HORIZON_DAYS * 24 * 3600_000;
  const sorted = slots
    .filter((s) => Date.parse(s.startsAt) <= horizon)
    .sort((x, y) => Date.parse(x.startsAt) - Date.parse(y.startsAt));
  const byDay = new Map<string, AvailabilitySlot[]>();
  for (const s of sorted) {
    const day = localDateString(Date.parse(s.startsAt), timezone);
    const list = byDay.get(day);
    if (list) list.push(s);
    else byDay.set(day, [s]);
  }
  const picked: AvailabilitySlot[] = [];
  for (const day of [...byDay.keys()].sort()) {
    const list = byDay.get(day)!;
    if (list.length <= MAX_SLOTS_PER_DAY) picked.push(...list);
    else {
      for (let k = 0; k < MAX_SLOTS_PER_DAY; k++) {
        picked.push(list[Math.floor(((k + 0.5) * list.length) / MAX_SLOTS_PER_DAY)]!);
      }
    }
  }
  return picked
    .slice(0, MAX_OFFERED_SLOTS)
    .map((slot, i) => ({ id: `S${i + 1}`, slot, line: `S${i + 1}: ${formatSlotLine(slot.startsAt, timezone)}` }));
}

export interface ScrubResult {
  text: string;
  /** Offered ids replaced by their human label. */
  replaced: string[];
  /** S-number tokens that matched no offer and were removed. */
  stripped: string[];
}

/**
 * ⚠️ SLOT IDS ARE THE SERVER'S BOOKING HANDLE AND MUST NEVER REACH A LEAD ("S10 at 4pm or S11 at 5pm"
 * went out in a live run). The prompt says so; this ENFORCES it before send. An offered id becomes the
 * slot's label ("Thu 16 Oct 4:00pm"); any other S-number token is removed.
 */
export function scrubSlotIds(reply: string, offers: readonly OfferedSlot[], timezone: string): ScrubResult {
  const replaced: string[] = [];
  const stripped: string[] = [];
  const text = reply
    .replace(/\bS\d{1,2}\b/g, (token) => {
      const offer = offers.find((o) => o.id === token);
      if (offer) {
        replaced.push(token);
        return formatSlotLine(offer.slot.startsAt, timezone);
      }
      stripped.push(token);
      return '';
    })
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\s+([,.!?])/g, '$1')
    .trim();
  return { text, replaced, stripped };
}

export function findOfferedSlot(offers: readonly OfferedSlot[], id: unknown): OfferedSlot | null {
  if (typeof id !== 'string') return null;
  return offers.find((o) => o.id === id.trim().toUpperCase()) ?? null;
}

export function composeBookingConfirmation(profile: BusinessProfile, whenText: string): string {
  const club = profile.club.displayName.trim() || 'the club';
  const address = profile.club.address.trim();
  const bring = profile.booking.whatToBring.trim();
  const where = profile.booking.whereToGo.trim();
  const sentence = (t: string): string => (/[.!?]$/.test(t) ? t : `${t}.`);
  // ⚠️ LABELS AROUND THE PROFILE FIELDS. The fields are fragments typed into a form ("ID, and proof of
  // address if you are starting a 7-day trial", "Reception"); pasted bare they read as the end of the
  // previous sentence ("... Reception."). A trailing full stop the club typed is dropped before the
  // label is added, so the sentence is not punctuated twice.
  const bare = (t: string): string => t.replace(/[.!?]+$/, '');
  return [
    `You're booked in for ${whenText} at ${club}${address !== '' ? `, ${address}` : ''}.`,
    bring !== '' ? sentence(`Bring ${bare(bring)}`) : '',
    where !== '' ? sentence(`When you arrive, head to ${bare(where)}`) : '',
    'We look forward to meeting you.',
  ]
    .filter((p) => p !== '')
    .join(' ');
}

/** Said when the slot was taken between offering and booking. Built by the server. */
export function composeSlotTaken(alternativeLines: readonly string[]): string {
  if (alternativeLines.length === 0) {
    return "Sorry, that time has just been taken. Let me get one of the team to find you another time.";
  }
  return `Sorry, that time has just been taken. The next available are ${alternativeLines.join(' or ')}. Which suits you?`;
}

/**
 * The confirmation for a visit booked into an ANYTIME FITNESS club calendar. ⚠️ SHORT ON PURPOSE: day, time and
 * the club. AF emails the lead its own confirmation and reminders (`remind_attendee`), carrying the address and
 * what to bring, so ours does not repeat them and adds no reminder of its own. Server template, never the model's text.
 */
export function composeAfBookingConfirmation(profile: BusinessProfile, whenText: string): string {
  const club = profile.club.displayName.trim() || 'the club';
  return `You're booked in for ${whenText} at ${club}. Anytime Fitness will email you a confirmation too. We look forward to meeting you.`;
}

/**
 * Said when the assistant cannot lock a time in itself (AF unreachable, refused, or its answer unclear) and a
 * person must. Deliberately promises nothing about the time: whether the visit exists is exactly what is unknown.
 */
export function composeBookingHandover(): string {
  return "Thanks, I'll get the team to lock that in for you and confirm shortly.";
}

/** The lead would rather not give an email address, so no time was offered: the team will arrange the visit. */
export function composeDetailsDeclinedHandover(): string {
  return "No worries at all, I'll get the team to sort a time with you and confirm shortly.";
}
